import React, { useContext } from 'react';

import { Card, Label } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getOnlineDevicesTestingNoteText } from 'client/blocks/TestingDateSelection/text';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { Context as SnapshotContext } from 'client/context/snapshotContext';
import { FetchingHandlerState } from 'client/hooks/useFetchingHandler';
import { getCompletedModerationStateTexts } from 'client/locale/ru/draftStatus';
import {
    DevicesTestingStatusMeta,
    initialDevicesTestingOptions,
    parseDevicesTestingOptions,
    TestingDateStatus,
} from 'client/model/devicesTestingForm';
import { SmartHome } from 'client/model/skill';
import { formatDevicesTestingRange } from 'client/utils/time';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeTestingDate.module.scss';
import { SmartHomeTestingDateChanger } from './SmartHomeTestingDateChanger';
import { SmartHomeTestingDateForm } from './SmartHomeTestingDateForm';

const t = i18n(keyset);

interface TestingDateSelectionSectionProps {
    aliceApp: SmartHome;
    state: FetchingHandlerState;
    devicesTestingStatusMeta: DevicesTestingStatusMeta | undefined;
    loadStatusUnhandled: () => Promise<void>;
    disabled: boolean;
}

export const SmartHomeTestingDate: React.FC<TestingDateSelectionSectionProps> = ({
    aliceApp,
    state,
    devicesTestingStatusMeta,
    loadStatusUnhandled,
    disabled,
}) => {
    const { refreshSnapshot } = useContext(SnapshotContext);

    return (
        <div>
            {(() => {
                switch (state) {
                    case 'loading':
                        return <PageLoader />;
                    case 'error':
                        return <Label theme="danger">{t('status-error')}</Label>;
                    case 'idle': {
                        if (!devicesTestingStatusMeta) {
                            return null;
                        }

                        const data = devicesTestingStatusMeta;

                        switch (data.status) {
                            case TestingDateStatus.NOT_SPECIFIED:
                                return (
                                    <SmartHomeTestingDateForm
                                        initialSettings={initialDevicesTestingOptions}
                                        afterSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.AWAITING:
                                return (
                                    <SmartHomeTestingDateChanger
                                        beforeButtonElement={<Label theme="info">{t('meeting-wait-approval')}</Label>}
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterButtonElement={
                                            <Card className={styles.smartHomeTestingDateCard} theme="warning">
                                                {getOnlineDevicesTestingNoteText()}
                                            </Card>
                                        }
                                        afterFormSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.REJECTED:
                                return (
                                    <SmartHomeTestingDateChanger
                                        beforeButtonElement={<Label theme="danger">{t('meeting-not-agreed')}</Label>}
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterFormSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.APPROVED: {
                                const formSettings = parseDevicesTestingOptions(data.record);

                                const approvedText = formSettings.selectedDateRange
                                    ? `${t('meeting-approved-on')}\xa0${formatDevicesTestingRange(
                                          formSettings.selectedDateRange,
                                          true,
                                      )}`
                                    : t('meeting-approved');

                                return (
                                    <SmartHomeTestingDateChanger
                                        beforeButtonElement={<Label theme="success">{approvedText}</Label>}
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterFormSave={refreshSnapshot}
                                        disabled={disabled}
                                    />
                                );
                            }
                            case TestingDateStatus.PASSED: {
                                return (
                                    <Label theme="success">
                                        {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            (getCompletedModerationStateTexts() as any)[aliceApp.draft.channel][
                                                aliceApp.draft.status
                                            ]
                                        }
                                    </Label>
                                );
                            }
                            default:
                                return null;
                        }
                    }
                }
            })()}
        </div>
    );
};
